import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { et, ru, enUS as en } from 'telia-front-react/locale';

import RootStore from '@stores/RootStore';
import RouteStore from '@stores/RouteStore';
import CookieApi from '@util/CookieUtil';

export type Language = 'et' | 'ru' | 'en';
export const ALLOWED_LANGUAGES: string[] = ['et', 'ru'];
export const LANGUAGES: string[] = ['et', 'ru'];
export const LANGUAGES_AS_LOCALES = {
  et,
  ru,
  en
};
export const DEFAULT_LANGUAGE = 'et';

export const languageSlug = (lang: string) => {
  return (!lang || lang === DEFAULT_LANGUAGE) ? '' : `/${lang}`;
};

const GUEST_LANGUAGE_COOKIE_NAME = 'LANGUAGE_EXT';

const LANGUAGE_ID_TO_LOCALE_MAP: { [segment: string]: string } = {
  et_EE: 'et',
  ru_RU: 'ru'
};

class LanguageStore {
  @observable accessor currentLanguage: Language;
  private routeStore: RouteStore;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.routeStore = rootStore.routeStore;
    runInAction(() => (this.currentLanguage = DEFAULT_LANGUAGE));
    this.init();
    reaction(
      () => this.currentLanguage,
      () => this.updateLangInPath()
    );
  }

  @computed
  get initialLanguageChanged() {
    return this.currentLanguage !== this.rootStore.initialState.currentLang;
  }

  public updateLangInPath() {
    return this.routeStore.setLanguage(this.currentLanguage);
  }

  @computed
  get currentLocale() {
    return LANGUAGES_AS_LOCALES[this.currentLanguage];
  }

  @action
  public setLanguage(language: Language) {
    if (!ALLOWED_LANGUAGES.includes(language)) {
      console.error(`Unsupported language: ${language}.`, 'Switching to default language: ', DEFAULT_LANGUAGE);
      language = DEFAULT_LANGUAGE;
    }
    this.currentLanguage = language;
    this.updateLanguageCookie(language);
    document.querySelector('html').setAttribute('lang', language);
  }

  private init() {
    const langFromPath = this.routeStore.langFromCurrentPath;
    if (langFromPath) {
      ALLOWED_LANGUAGES.includes(langFromPath) ? this.setLanguage(langFromPath as Language) : this.setLanguage(DEFAULT_LANGUAGE);
    }
    const storedLang = this.getLanguageFromCookie();
    if (storedLang && storedLang !== this.currentLanguage) {
      this.setLanguage(storedLang as Language);
    }
    this.updateLangInPath();
  }

  private resolveLocale = (languageId: string) => {
    return LANGUAGE_ID_TO_LOCALE_MAP[languageId] as Language;
  };

  private resolveLanguageId = (language: Language) => {
    return Object.keys(LANGUAGE_ID_TO_LOCALE_MAP).find((key) => LANGUAGE_ID_TO_LOCALE_MAP[key] === language);
  };

  private getLanguageFromCookie() {
    return this.resolveLocale(CookieApi.getCookieValue(GUEST_LANGUAGE_COOKIE_NAME) as Language);
  }

  private updateLanguageCookie(language: Language) {
    if (CookieApi.getCookieValue(GUEST_LANGUAGE_COOKIE_NAME) !== language) {
      const languageId = this.resolveLanguageId(language);
      const expirationDate = new Date();
      expirationDate.setFullYear(new Date().getFullYear() + 1);
      return CookieApi.setCookie(GUEST_LANGUAGE_COOKIE_NAME, languageId!, expirationDate.toUTCString());
    }
  }
}

export default LanguageStore;
